import { ApiRetailerPaymentType, ApiTransaction } from '@vend/types'
import { trackError, trackUserEvent } from '@vend/utilities'
import {
  getMainApiTender,
  getSurchargeRuleFromTenders,
  getTotalPaymentAmountFromTenders,
  getTotalSurchargeAmountFromTenders,
  getTotalTipAmountFromTenders,
} from '../../../../functions/payments'
import { WEB_REGISTER } from '../../../web-register.constant'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import { SalePayment } from '../PaymentModal/types'
import {
  ExternalEmvData,
  ExternalPaymentData,
} from '../../../../models/payment'

export const useHandleSwiperPaymentSuccess = () => {
  return ({
    transaction,
    paymentType,
    amountTendered,
  }: {
    transaction: ApiTransaction
    paymentType: ApiRetailerPaymentType
    amountTendered: number
  }): SalePayment | undefined => {
    if (!transaction || !transaction.tenders) {
      trackError('[Payment] Missing required Swiper transaction details', {
        data: { transaction },
      })

      return
    }

    const mainTender = getMainApiTender(transaction.tenders)

    if (!mainTender) {
      trackError('[Payment] Missing main tender from swiper transaction')

      return
    }

    trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Swiper Payment Completed', {
      paymentTypeId: paymentType.type_id,
      transactionId: transaction.id,
      amount: amountTendered,
    })

    const lspaySurchargeOn = getFeatureFromCache('lspay_surcharge')

    let emv: ExternalEmvData | undefined
    if (mainTender.emv) {
      let emvTags: Array<{ key: string; value: string }> | undefined
      if (mainTender.emv.tags) {
        emvTags = mainTender.emv.tags
      }

      emv = {
        ...mainTender.emv,
        tags: emvTags,
      }
    }

    const transactionData: ExternalPaymentData = {
      // @note that the register_sale_payment_id is the same as the transactionId (uuid) that is generated within the
      // swiper payment modal controller when performing the inital swiper request.
      source: 'Swiper',
      source_id: transaction.id,
      payment_id: transaction.register_sale_payment_id,
      external_transaction_id: mainTender.external_transaction_id,
      signature_url: mainTender.provider_fields.signature_url
        ? mainTender.provider_fields.signature_url
        : null,
      transaction_status: mainTender.status,
      provider_fields: mainTender.provider_fields,
      card: {
        entry_method: mainTender.card_details.entry_method,
        brand: mainTender.card_details.card?.brand,
        type: mainTender.card_details.card?.type,
        last_four: mainTender.card_details.card?.last_four,
      },
      emv,
    }

    let paymentAmount: number
    let tipAmount = 0
    let surchargeAmount = 0

    if (mainTender.type === 'REFUND') {
      paymentAmount = -mainTender.amount
    } else {
      paymentAmount = getTotalPaymentAmountFromTenders(transaction.tenders)
      tipAmount = getTotalTipAmountFromTenders(transaction.tenders)
      surchargeAmount = lspaySurchargeOn
        ? getTotalSurchargeAmountFromTenders(transaction.tenders)
        : 0
    }

    const surchargeRule = surchargeAmount
      ? getSurchargeRuleFromTenders(transaction.tenders)
      : ''

    const surchargeData = {
      amount: surchargeAmount,
      rule: surchargeRule,
    }

    return {
      paymentAmount,
      paymentType,
      transactionData,
      tipAmount,
      surchargeData,
    }
  }
}
